import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthService from "../../../api/services/AuthService";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { ProfileContext } from "../../../context/ProfileProvider";
import { isKycVerified, kycTradeCtaLabel } from "../../../utils/kycVerified";
import { openKycVerification } from "../KycVara/openKycVerification";
import { decNum, decStr } from "./optionsDataHelpers";
import { useVipTradeFees } from "../TradePage/useVipTradeFees";
import {
  computeOrderReserveUsdt,
  estimateOptionsOrderCost,
  floorToIncrement,
  formatFeeRateDisplay,
  formatOrderDecimal,
  getOptionsPriceBandError,
  getOptionsReduceOnlyTooltip,
  hasConfiguredIncrement,
  INSUFFICIENT_FUNDS_MSG,
  isOptionsLongPosition,
  maxAffordableOptionsQuantity,
  newOptionsIdempotencyKey,
  parseOptionsApiError,
  resolveIncrementSize,
  resolveContractFeeRates,
  resolveOptionsFormReduceOnly,
  sanitizeIncrementInput,
  SELL_REQUIRES_LONG_HINT,
  SHORT_NOT_SUPPORTED_MSG,
  snapToIncrementInput,
  validateOptionsPlaceOrder,
} from "./optionsOrderValidation";

const TIF_OPTIONS = ["GTC", "IOC", "FOK"];
const PCT_STEPS = [0, 25, 50, 75, 100];

function fmtNum(val, decimals = 2) {
  const n = decNum(val);
  if (!Number.isFinite(n)) return "0.00";
  return n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export default function OptionsOrderForm({
  orderSide,
  setOrderSide,
  token,
  accountUpdate,
  obLastPrice,
  obIvPct,
  selectedAsset,
  selectedContractSymbol,
  selectedContractRaw,
  tickSize: tickSizeProp,
  stepSize: stepSizeProp,
  indexPrice,
  feeRates,
  symbolPosition = null,
  symbolOpenOrders = [],
  onTransferClick,
  onOrderAcknowledged,
}) {
  const navigate = useNavigate();
  const { userDetails } = useContext(ProfileContext) || {};
  const kycStatus = userDetails?.kycVerified;
  const needsKyc = !isKycVerified(kycStatus);
  const kycButtonLabel = kycTradeCtaLabel(kycStatus);

  const [orderPrice, setOrderPrice] = useState("");
  const [orderAmount, setOrderAmount] = useState("");
  const [amountPct, setAmountPct] = useState(0);
  const [orderTif, setOrderTif] = useState("GTC");
  const [tifOpen, setTifOpen] = useState(false);
  const tifRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [showFeeTooltip, setShowFeeTooltip] = useState(false);
  const feeTooltipRef = useRef(null);
  const priceUserEditedRef = useRef(false);
  /** One-time prefill per contract — live mark ticks must not overwrite price. */
  const prefilledForSymbolRef = useRef(null);

  // Close TIF dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (tifRef.current && !tifRef.current.contains(event.target)) {
        setTifOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const tickSize = useMemo(
    () => resolveIncrementSize(tickSizeProp, selectedContractRaw, "tick_size"),
    [tickSizeProp, selectedContractRaw]
  );
  const stepSize = useMemo(
    () => resolveIncrementSize(stepSizeProp, selectedContractRaw, "step_size"),
    [stepSizeProp, selectedContractRaw]
  );
  const hasTickConfig = useMemo(
    () => hasConfiguredIncrement(tickSizeProp, selectedContractRaw, "tick_size"),
    [tickSizeProp, selectedContractRaw]
  );
  const hasStepConfig = useMemo(
    () => hasConfiguredIncrement(stepSizeProp, selectedContractRaw, "step_size"),
    [stepSizeProp, selectedContractRaw]
  );

  const effectiveFeeRates = useMemo(
    () => resolveContractFeeRates(selectedContractRaw, feeRates),
    [selectedContractRaw, feeRates]
  );
  const optionsVip = useVipTradeFees("options");

  const avblUsdt = useMemo(() => {
    if (!token || !accountUpdate) return 0;
    return decNum(accountUpdate.available_balance);
  }, [token, accountUpdate]);

  const underlyingPair = selectedAsset ? `${selectedAsset}USDT` : "BTCUSDT";
  const contractHint = `1 Cont = 1 ${underlyingPair}`;

  useEffect(() => {
    priceUserEditedRef.current = false;
    prefilledForSymbolRef.current = null;
    setOrderPrice("");
  }, [selectedContractSymbol]);

  useEffect(() => {
    if (!selectedContractSymbol || priceUserEditedRef.current || !hasTickConfig) return;
    if (prefilledForSymbolRef.current === selectedContractSymbol) return;

    const mark = decNum(selectedContractRaw?.mark_price);
    const ref =
      obLastPrice != null && obLastPrice > 0
        ? obLastPrice
        : mark > 0
          ? mark
          : null;
    if (ref == null || ref <= 0) return;

    const snapped = snapToIncrementInput(String(ref), tickSize);
    if (snapped) {
      setOrderPrice(snapped);
      prefilledForSymbolRef.current = selectedContractSymbol;
    }
  }, [
    selectedContractSymbol,
    obLastPrice,
    selectedContractRaw?.mark_price,
    tickSize,
    hasTickConfig,
  ]);

  useEffect(() => {
    setAmountPct(0);
    setOrderAmount("");
  }, [selectedContractSymbol, orderSide]);

  const apiSide = orderSide === "buy" ? "BUY" : "SELL";
  const hasLong = isOptionsLongPosition(symbolPosition, selectedContractSymbol);
  const reduceOnlyState = useMemo(
    () =>
      resolveOptionsFormReduceOnly({
        side: apiSide,
        position: symbolPosition,
        symbol: selectedContractSymbol,
      }),
    [apiSide, symbolPosition, selectedContractSymbol]
  );
  const effectiveReduceOnly = reduceOnlyState.reduceOnly;
  const reduceOnlyTooltip = useMemo(
    () =>
      getOptionsReduceOnlyTooltip({
        side: apiSide,
        position: symbolPosition,
        symbol: selectedContractSymbol,
      }),
    [apiSide, symbolPosition, selectedContractSymbol]
  );

  const sellBlocked = orderSide === "sell" && !hasLong;

  const maxContracts = useMemo(
    () =>
      maxAffordableOptionsQuantity({
        availableBalance: avblUsdt,
        price: orderPrice,
        stepSize,
        side: apiSide,
        orderType: "LIMIT",
        indexPrice,
        markPrice: selectedContractRaw?.mark_price ?? orderPrice,
        strike: selectedContractRaw?.strike,
        optionType: selectedContractRaw?.option_type,
        contractSize: selectedContractRaw?.contract_size ?? "1",
        feeRates: effectiveFeeRates,
        position: symbolPosition,
        openOrders: symbolOpenOrders,
        symbol: selectedContractSymbol,
      }),
    [
      avblUsdt,
      orderPrice,
      stepSize,
      apiSide,
      indexPrice,
      selectedContractRaw,
      effectiveFeeRates,
      symbolPosition,
      symbolOpenOrders,
      selectedContractSymbol,
    ]
  );

  const applyPct = useCallback(
    (pct) => {
      setAmountPct(pct);
      if (pct === 0) {
        setOrderAmount("");
        return;
      }
      if (maxContracts <= 0) {
        setOrderAmount("");
        return;
      }
      const qty = floorToIncrement((maxContracts * pct) / 100, stepSize);
      setOrderAmount(qty > 0 ? formatOrderDecimal(qty, stepSize) : "");
    },
    [maxContracts, stepSize]
  );

  const normalizedPreview = useMemo(() => {
    const price = floorToIncrement(orderPrice, tickSize);
    const qty = floorToIncrement(orderAmount, stepSize);
    const side = apiSide;
    const costEst = estimateOptionsOrderCost({
      side,
      price,
      quantity: qty,
      orderType: "LIMIT",
      indexPrice,
      markPrice: selectedContractRaw?.mark_price ?? price,
      strike: selectedContractRaw?.strike,
      optionType: selectedContractRaw?.option_type,
      contractSize: selectedContractRaw?.contract_size ?? "1",
      feeRates: effectiveFeeRates,
      position: symbolPosition,
      openOrders: symbolOpenOrders,
      symbol: selectedContractSymbol,
    });
    return {
      price,
      qty,
      side,
      costEst,
      reserve: costEst?.total_estimated_cost ?? computeOrderReserveUsdt(price, qty, "LIMIT"),
    };
  }, [
    orderPrice,
    orderAmount,
    tickSize,
    stepSize,
    apiSide,
    selectedContractRaw,
    indexPrice,
    effectiveFeeRates,
    symbolPosition,
    symbolOpenOrders,
    selectedContractSymbol,
  ]);

  const costDisplay = useMemo(() => {
    if (sellBlocked) return "0.00";
    if (normalizedPreview.costEst?.scenario === "CLOSE_LONG") return "0.00";
    if (normalizedPreview.qty <= 0 || normalizedPreview.price <= 0) return "--";
    return fmtNum(normalizedPreview.reserve, 4);
  }, [normalizedPreview, sellBlocked]);

  const closeLongFeeHint = useMemo(() => {
    const est = normalizedPreview.costEst;
    if (est?.scenario !== "CLOSE_LONG") return null;
    const fee = decNum(est.estimated_transaction_fee);
    if (fee <= 0) return null;
    return `Est. fee on fill: ${fmtNum(fee, 4)} USDT`;
  }, [normalizedPreview.costEst]);

  const displayFeeRates = useMemo(() => {
    const rates = effectiveFeeRates || {};
    const contractTx = formatFeeRateDisplay(rates.transaction_fee_rate ?? rates.taker_fee_rate, 4);
    const transaction = !optionsVip.ready
      ? "---"
      : optionsVip.fees?.taker || contractTx;
    return {
      transaction,
      exercise: formatFeeRateDisplay(rates.exercise_fee_rate, 3),
      liquidation: formatFeeRateDisplay(rates.liquidation_fee_rate, 2),
    };
  }, [effectiveFeeRates, optionsVip]);

  useEffect(() => {
    if (!showFeeTooltip) return undefined;
    const onDocClick = (e) => {
      if (feeTooltipRef.current && !feeTooltipRef.current.contains(e.target)) {
        setShowFeeTooltip(false);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [showFeeTooltip]);

  const ivDisplay = obIvPct != null && obIvPct > 0 ? `${obIvPct.toFixed(1)}%` : "--";

  const priceBandError = useMemo(() => {
    const snapped = snapToIncrementInput(orderPrice, tickSize);
    const checkPrice = snapped || orderPrice;
    if (!checkPrice || decNum(checkPrice) <= 0) return null;
    return getOptionsPriceBandError(
      checkPrice,
      apiSide,
      selectedContractRaw,
      tickSize
    );
  }, [orderPrice, apiSide, selectedContractRaw, tickSize]);

  const formValidation = useMemo(() => {
    if (!selectedContractSymbol || !selectedContractRaw) {
      return { ok: false, insufficientFunds: false };
    }
    if (sellBlocked) {
      return { ok: false, insufficientFunds: false, message: SHORT_NOT_SUPPORTED_MSG };
    }
    return validateOptionsPlaceOrder({
      symbol: selectedContractSymbol,
      side: apiSide,
      orderType: "LIMIT",
      price: orderPrice,
      quantity: orderAmount,
      timeInForce: orderTif,
      reduceOnly: effectiveReduceOnly,
      availableBalance: token ? avblUsdt : 0,
      contract: selectedContractRaw,
      tickSize,
      stepSize,
      indexPrice,
      feeRates: effectiveFeeRates,
      position: symbolPosition,
      openOrders: symbolOpenOrders,
    });
  }, [
    selectedContractSymbol,
    selectedContractRaw,
    sellBlocked,
    apiSide,
    orderPrice,
    orderAmount,
    orderTif,
    effectiveReduceOnly,
    token,
    avblUsdt,
    tickSize,
    stepSize,
    indexPrice,
    effectiveFeeRates,
    symbolPosition,
    symbolOpenOrders,
  ]);

  const isSubmitDisabled =
    submitting
    || !token
    || !selectedContractSymbol
    || sellBlocked
    || !formValidation.ok;

  const insufficientFundsError =
    token && formValidation.insufficientFunds ? INSUFFICIENT_FUNDS_MSG : null;

  const snapPriceField = useCallback(
    (raw) => {
      const trimmed = String(raw ?? "").trim();
      if (!trimmed) return "";
      return snapToIncrementInput(trimmed, tickSize);
    },
    [tickSize]
  );

  const snapAmountField = useCallback(
    (raw) => {
      const trimmed = String(raw ?? "").trim();
      if (!trimmed) return "";
      return snapToIncrementInput(trimmed, stepSize);
    },
    [stepSize]
  );

  const handlePriceBlur = useCallback(() => {
    setOrderPrice((prev) => snapPriceField(prev));
  }, [snapPriceField]);

  const handleAmountBlur = useCallback(() => {
    setOrderAmount((prev) => snapAmountField(prev));
  }, [snapAmountField]);

  const handleSubmit = async () => {
    if (!token) {
      alertErrorMessage("Please log in to place an order.");
      return;
    }
    if (needsKyc) {
      openKycVerification();
      return;
    }
    if (sellBlocked) {
      alertErrorMessage(SHORT_NOT_SUPPORTED_MSG);
      return;
    }

    const snappedPrice = snapPriceField(orderPrice);
    const snappedAmount = snapAmountField(orderAmount);
    if (snappedPrice !== orderPrice) setOrderPrice(snappedPrice);
    if (snappedAmount !== orderAmount) setOrderAmount(snappedAmount);

    const validation = validateOptionsPlaceOrder({
      symbol: selectedContractSymbol,
      side: apiSide,
      orderType: "LIMIT",
      price: snappedPrice,
      quantity: snappedAmount,
      timeInForce: orderTif,
      reduceOnly: effectiveReduceOnly,
      availableBalance: avblUsdt,
      contract: selectedContractRaw,
      tickSize,
      stepSize,
      indexPrice,
      feeRates: effectiveFeeRates,
      position: symbolPosition,
      openOrders: symbolOpenOrders,
    });

    if (!validation.ok) {
      if (validation.message !== INSUFFICIENT_FUNDS_MSG) {
        alertErrorMessage(validation.message);
      }
      return;
    }

    setSubmitting(true);
    try {
      const idempotencyKey = newOptionsIdempotencyKey();
      const res = await AuthService.placeOptionOrder(validation.payload, idempotencyKey);
      if (res?.success) {
        alertSuccessMessage(`${orderSide === "buy" ? "Buy" : "Sell"} order submitted.`);
        setOrderAmount("");
        setAmountPct(0);
        if (validation.normalized?.price > 0) {
          setOrderPrice(formatOrderDecimal(validation.normalized.price, tickSize));
        }
        onOrderAcknowledged?.();
      } else {
        alertErrorMessage(parseOptionsApiError(res));
      }
    } catch {
      alertErrorMessage("Order failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const priceHint = hasTickConfig ? `Tick ${decStr(tickSize)}` : null;
  const stepHint = hasStepConfig ? `Step ${decStr(stepSize)}` : null;

  return (
    <div className="agcex-opt-orderform">
      <div className="agcex-opt-form-tabs">
        <div
          className={`agcex-opt-form-tab buy ${orderSide === "buy" ? "active" : ""}`}
          onClick={() => setOrderSide("buy")}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && setOrderSide("buy")}
        >
          Buy
        </div>
        <div
          className={`agcex-opt-form-tab sell ${orderSide === "sell" ? "active" : ""}`}
          onClick={() => setOrderSide("sell")}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && setOrderSide("sell")}
        >
          Sell
        </div>
      </div>

      <div className="agcex-opt-form-row agcex-opt-avbl-row">
        <span>Avbl</span>
        <span className="agcex-opt-form-val">
          {token && accountUpdate ? fmtNum(avblUsdt, 2) : "0.00"} USDT
          <i
            className="ri-arrow-left-right-line agcex-opt-transfer-icon agcex-opt-transfer-icon--gold"
            role="button"
            tabIndex={0}
            aria-label="Transfer funds"
            onClick={onTransferClick}
            onKeyDown={(e) => e.key === "Enter" && onTransferClick?.()}
          />
        </span>
      </div>

      <div className="agcex-opt-input-group">
        <div className="agcex-opt-input-label">
          Price <i className="ri-information-line agcex-opt-label-info" aria-hidden="true" />
          {priceHint && <span className="agcex-opt-field-hint">{priceHint}</span>}
        </div>
        <div className={`agcex-opt-input-wrap${priceBandError ? " agcex-opt-input-wrap--error" : ""}`}>
          <input
            type="text"
            inputMode="decimal"
            value={orderPrice}
            aria-invalid={priceBandError ? "true" : undefined}
            onChange={(e) => {
              priceUserEditedRef.current = true;
              setOrderPrice(sanitizeIncrementInput(e.target.value, tickSize));
              setAmountPct(0);
            }}
            onBlur={handlePriceBlur}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handlePriceBlur();
              }
            }}
          />
          <span className="agcex-opt-input-addon">USDT</span>
        </div>
        {priceBandError && (
          <p className="agcex-opt-price-band-msg">{priceBandError}</p>
        )}
      </div>

      <div className="agcex-opt-iv-row">
        <span>
          IV <i className="ri-information-line agcex-opt-label-info" aria-hidden="true" />
        </span>
        <span className="agcex-opt-iv-row-val">{ivDisplay}</span>
      </div>

      <div className="agcex-opt-input-group">
        <div className="agcex-opt-input-label">
          Amount
          {stepHint && <span className="agcex-opt-field-hint">{stepHint}</span>}
        </div>
        <div className="agcex-opt-input-wrap agcex-opt-input-wrap--amount">
          <input
            type="text"
            inputMode="decimal"
            placeholder={contractHint}
            value={orderAmount}
            onChange={(e) => {
              setOrderAmount(sanitizeIncrementInput(e.target.value, stepSize));
              setAmountPct(0);
            }}
            onBlur={handleAmountBlur}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAmountBlur();
              }
            }}
          />
          <span className="agcex-opt-input-addon">Contract</span>
        </div>
      </div>

      <div className="agcex-opt-pct-slider">
        <div className="agcex-opt-pct-slider-track" aria-hidden="true" />
        <div className="agcex-opt-pct-slider-ticks">
          {PCT_STEPS.map((pct) => (
            <button
              key={pct}
              type="button"
              className={`agcex-opt-pct-tick ${amountPct === pct ? "is-active" : ""}`}
              aria-label={`${pct}%`}
              onClick={() => applyPct(pct)}
            />
          ))}
        </div>
      </div>

      <div className="agcex-opt-reduce-tif-row">
        {orderSide === "sell" && (
          <label
            className="agcex-opt-reduce-only is-locked"
            title={reduceOnlyTooltip}
          >
            <input
              type="checkbox"
              checked
              disabled
              readOnly
              aria-label="Reduce Only"
            />
            <span className="agcex-opt-iv-label agcex-opt-reduce-only-label">
              Reduce Only
              <i
                className="ri-information-line agcex-opt-label-info"
                aria-hidden="true"
                title={reduceOnlyTooltip}
              />
            </span>
          </label>
        )}
        <div className="agcex-opt-tif-select" ref={tifRef}>
          <span>TIF</span>
          <div className="agcex-opt-tif-value" onClick={() => setTifOpen(!tifOpen)}>
            {orderTif}
            <i className={`ri-arrow-down-s-line ${tifOpen ? "open" : ""}`} aria-hidden="true" />
          </div>
          {tifOpen && (
            <ul className="agcex-opt-tif-options">
              {TIF_OPTIONS.map((tif) => (
                <li
                  key={tif}
                  className={orderTif === tif ? "active" : ""}
                  onClick={() => {
                    setOrderTif(tif);
                    setTifOpen(false);
                  }}
                >
                  {tif}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {insufficientFundsError && (
        <p className="agcex-opt-form-error-msg">{insufficientFundsError}</p>
      )}
      {sellBlocked && token && !needsKyc && (
        <p className="agcex-opt-form-error-msg">{SELL_REQUIRES_LONG_HINT}</p>
      )}

      {!token ? (
        <button
          type="button"
          className={`agcex-opt-submit-btn ${orderSide === "buy" ? "buy" : "sell"}`}
          onClick={() => navigate("/login")}
        >
          Login
        </button>
      ) : needsKyc ? (
        <button
          type="button"
          onClick={openKycVerification}
          className={`agcex-opt-submit-btn ${orderSide === "buy" ? "buy" : "sell"} agcex-opt-submit-btn--kyc`}
        >
          {kycButtonLabel}
        </button>
      ) : (
        <button
          type="button"
          className={`agcex-opt-submit-btn ${orderSide === "buy" ? "buy" : "sell"}${isSubmitDisabled ? " is-disabled" : ""}`}
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
        >
          {submitting ? "…" : orderSide === "buy" ? "Buy" : "Sell"}
        </button>
      )}

      <div className="agcex-opt-form-footer">
        <span>
          Cost {costDisplay === "--" ? "--" : costDisplay}
          {closeLongFeeHint ? (
            <span className="text-muted small"> · {closeLongFeeHint}</span>
          ) : null}
        </span>
        <div className="agcex-opt-fee-rate-wrap" ref={feeTooltipRef}>
          <button
            type="button"
            className="agcex-opt-fee-rate"
            aria-expanded={showFeeTooltip}
            onMouseEnter={() => setShowFeeTooltip(true)}
            onMouseLeave={() => setShowFeeTooltip(false)}
            onClick={(e) => {
              e.stopPropagation();
              setShowFeeTooltip((v) => !v);
            }}
          >
            Fee Rate
          </button>
          {showFeeTooltip && (
            <div className="agcex-opt-fee-tooltip" role="tooltip">
              <div className="agcex-opt-fee-tooltip-title">Fee Rate</div>
              <div className="agcex-opt-fee-tooltip-row">
                <span>Transaction Fee Rate</span>
                <span>{displayFeeRates.transaction}</span>
              </div>
              <div className="agcex-opt-fee-tooltip-row">
                <span>Exercise Fee Rate</span>
                <span>{displayFeeRates.exercise}</span>
              </div>
              <div className="agcex-opt-fee-tooltip-row">
                <span>Liquidation Fee Rate</span>
                <span>{displayFeeRates.liquidation}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
